import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "../supabase";
import { C, font, mono } from "../config/theme";
import { fmt } from "../utils/format";
import KpiCard from "../components/KpiCard";
import MiniTable from "../components/MiniTable";
import DrillDown from "../components/DrillDown";
import StatusMessage from "../components/StatusMessage";

/* ─── Couleurs sévérité ─── */
const SEVERITE = {
  critique: { bg: C.redBg,   color: C.red,     border: C.red,    label: "Critique" },
  warning:  { bg: "#FFFBEB", color: "#92400E",  border: "#F59E0B", label: "Warning" },
  info:     { bg: "#EFF6FF", color: "#1E40AF",  border: C.blue,   label: "Info" },
};

function SeveriteBadge({ severite }) {
  const v = SEVERITE[severite] || { bg: C.bg, color: C.slate, border: C.border, label: severite };
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
      background: v.color, color: "#fff", whiteSpace: "nowrap",
    }}>
      {v.label}
    </span>
  );
}

/* ─── Labels types d'alertes ─── */
const TYPE_ALERTE_LABELS = {
  mo_depassee:            "MO dépassée",
  marge_negative:         "Marge négative",
  marge_faible:           "Marge faible",
  bl_non_factures:        "BL non facturés",
  sous_facturation:       "Sous-facturation",
  interim_excessif:       "Intérim excessif",
  sans_facturation:       "Sans facturation",
  ecart_chiffrage_marche: "Écart chiffrage/marché",
};
const typeLabel = (t) => TYPE_ALERTE_LABELS[t] || t;

/* ─── Section ─── */
function SectionTitle({ children, color = C.navy }) {
  return (
    <div style={{
      fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px",
      color, margin: "24px 0 12px", paddingBottom: 8, borderBottom: `2px solid ${color}20`,
    }}>
      {children}
    </div>
  );
}

/* ─── Sub-section bandeau (Détection) ─── */
function SubSectionTitle({ children, color = C.slate, badge }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      margin: "16px 0 8px",
    }}>
      <div style={{
        fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color,
      }}>
        {children}
      </div>
      {badge && (
        <span style={{
          padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700,
          background: `${color}20`, color, whiteSpace: "nowrap",
        }}>
          {badge}
        </span>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   PAGE ALERTES — Monitoring + détection automatique
   ═══════════════════════════════════════════════════════════════════════════════ */
export default function PageAlertes() {
  /* ─── Filtres ─── */
  const [filSeverite, setFilSeverite] = useState("");
  const [filType, setFilType] = useState("");
  const [filConducteur, setFilConducteur] = useState("");
  const [includeResolues, setIncludeResolues] = useState(false);

  /* ─── Données ─── */
  const [filtresData, setFiltresData] = useState(null); // pour conducteurs
  const [global, setGlobal] = useState(null);
  const [liste, setListe] = useState(null);
  const [detection, setDetection] = useState(null);

  /* ─── États ─── */
  const [loadingInit, setLoadingInit] = useState(true);
  const [loadingListe, setLoadingListe] = useState(false);
  const [error, setError] = useState(null);

  /* ─── Params RPC liste ─── */
  const paramsListe = useMemo(() => ({
    ...(filSeverite    ? { p_severite: filSeverite } : {}),
    ...(filType        ? { p_type_alerte: filType } : {}),
    ...(filConducteur  ? { p_conducteur_id: filConducteur } : {}),
    p_actives_seulement: !includeResolues,
  }), [filSeverite, filType, filConducteur, includeResolues]);

  /* ─── Chargement initial : global + détection + filtres ref + liste initiale ─── */
  useEffect(() => {
    async function loadInit() {
      setLoadingInit(true);
      try {
        const [rGlobal, rDetect, rFiltres] = await Promise.all([
          supabase.rpc("bv_rpc_alertes_global"),
          supabase.rpc("bv_rpc_alertes_detection"),
          supabase.rpc("bv_rpc_ca_filtres"),
        ]);
        if (rGlobal.data) setGlobal(rGlobal.data);
        if (rDetect.data) setDetection(rDetect.data);
        if (rFiltres.data) setFiltresData(rFiltres.data);
        [rGlobal, rDetect, rFiltres].forEach((r, i) => {
          if (r.error) console.error(`Erreur RPC init alertes #${i + 1}:`, r.error);
        });
      } catch (err) {
        console.error("Erreur init alertes:", err);
        setError("Erreur lors du chargement initial.");
      } finally {
        setLoadingInit(false);
      }
    }
    loadInit();
  }, []);

  /* ─── Rechargement liste quand les filtres changent ─── */
  useEffect(() => {
    async function loadListe() {
      setLoadingListe(true);
      const { data, error: err } = await supabase.rpc("bv_rpc_alertes_liste", paramsListe);
      if (err) {
        console.error("Erreur RPC alertes_liste:", err);
        setError("Erreur lors du chargement de la liste.");
      } else if (data) {
        setListe(data);
      }
      setLoadingListe(false);
    }
    loadListe();
  }, [paramsListe]);

  /* ─── Types d'alertes uniques (pour le filtre dropdown) ─── */
  const typesAlerte = useMemo(() => {
    const seen = new Set();
    (liste?.par_type || []).forEach((r) => seen.add(r.type_alerte));
    // Inclure aussi les types existants en détection automatique
    return [...seen].map((t) => ({ value: t, label: typeLabel(t) }));
  }, [liste]);

  /* ─── Colonnes DrillDown alertes actives — 6 colonnes essentielles ─── */
  /* Retirés : code, activité, seuil, actuelle, déclenchée (présents dans le message
     et accessibles via les filtres colonnes si besoin de filtrer) */
  const colonnesAlertes = useMemo(() => [
    { key: "severite", label: "Sévérité", render: (r) => <SeveriteBadge severite={r.severite} /> },
    { key: "chantier", label: "Chantier", bold: true },
    { key: "type_alerte", label: "Type", render: (r) => typeLabel(r.type_alerte) },
    {
      key: "message", label: "Message",
      render: (r) => (
        <div
          title={r.message}
          style={{
            maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
        >
          {r.message}
        </div>
      ),
    },
    { key: "conducteur", label: "Conducteur" },
    { key: "anciennete_jours", label: "Ancienneté", align: "right", mono: true,
      render: (r) => `${r.anciennete_jours || 0} j`,
      color: (r) => (r.anciennete_jours || 0) > 14 ? C.red : (r.anciennete_jours || 0) > 7 ? C.accent : C.slate },
  ], []);

  /* ═══════════════════════════════════════════════════════════════════════════
     RENDU
     ═══════════════════════════════════════════════════════════════════════════ */
  if (loadingInit) {
    return <div style={{ padding: "40px 0", textAlign: "center", color: C.slate }}>Chargement…</div>;
  }
  if (error) {
    return <StatusMessage type="error" message={error} action={{ label: "Réessayer", onClick: () => window.location.reload() }} />;
  }

  return (
    <div>
      {/* ─── En-tête ─── */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.text }}>Alertes</h1>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: C.slate }}>
          Monitoring temps réel et détection automatique des risques
        </p>
      </div>

      {/* ═══ KPI — grid 3 colonnes (auto-wrap : 3 + 2) ═══ */}
      {/* minmax(0, 1fr) au lieu de 1fr → empêche le débordement horizontal
          dû au minWidth:200 du KpiCard sur les viewports étroits */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 16, marginBottom: 16 }}>
        <KpiCard
          label="Alertes ouvertes"
          value={global?.nb_ouvertes || 0}
          color={(global?.nb_ouvertes || 0) > 0 ? C.accent : C.slateLight}
          isCurrency={false}
        />
        <KpiCard
          label="Critiques"
          value={global?.nb_critiques || 0}
          color={(global?.nb_critiques || 0) > 0 ? C.red : C.slateLight}
          isCurrency={false}
        />
        <KpiCard
          label="Warnings"
          value={global?.nb_warning || 0}
          color={(global?.nb_warning || 0) > 0 ? C.accent : C.slateLight}
          isCurrency={false}
        />
        <KpiCard
          label="Info"
          value={global?.nb_info || 0}
          color={C.blue}
          isCurrency={false}
        />
        <KpiCard
          label="Délai moyen résolution"
          value={global?.delai_moyen_resolution || 0}
          color={C.slate}
          isCurrency={false}
          suffix=" j"
        />
      </div>

      {/* ═══ FILTRES ═══ */}
      <div style={{
        display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap",
        marginBottom: 16, padding: "14px 18px",
        background: C.surface, borderRadius: 12, border: `1px solid ${C.border}`,
      }}>
        {/* Sévérité */}
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <label style={{ fontSize: 10, fontWeight: 600, color: C.slate, textTransform: "uppercase" }}>Sévérité</label>
          <select
            value={filSeverite}
            onChange={(e) => setFilSeverite(e.target.value)}
            style={{
              padding: "6px 10px", borderRadius: 8, border: `1px solid ${C.border}`,
              fontSize: 12, color: C.text, fontFamily: font, minWidth: 130, background: C.surface,
            }}
          >
            <option value="">Toutes</option>
            <option value="critique">Critique</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
        </div>

        {/* Type d'alerte */}
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <label style={{ fontSize: 10, fontWeight: 600, color: C.slate, textTransform: "uppercase" }}>Type</label>
          <select
            value={filType}
            onChange={(e) => setFilType(e.target.value)}
            style={{
              padding: "6px 10px", borderRadius: 8, border: `1px solid ${C.border}`,
              fontSize: 12, color: C.text, fontFamily: font, minWidth: 180, background: C.surface,
            }}
          >
            <option value="">Tous les types</option>
            {typesAlerte.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        {/* Conducteur */}
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <label style={{ fontSize: 10, fontWeight: 600, color: C.slate, textTransform: "uppercase" }}>Conducteur</label>
          <select
            value={filConducteur}
            onChange={(e) => setFilConducteur(e.target.value)}
            style={{
              padding: "6px 10px", borderRadius: 8, border: `1px solid ${C.border}`,
              fontSize: 12, color: C.text, fontFamily: font, minWidth: 170, background: C.surface,
            }}
          >
            <option value="">Tous</option>
            {(filtresData?.conducteurs || []).map((c) => (
              <option key={c.value || c.id} value={c.value || c.id}>{c.label}</option>
            ))}
          </select>
        </div>

        {/* Séparateur */}
        <div style={{ width: 1, height: 36, background: C.border }} />

        {/* Toggle inclure résolues */}
        <label style={{
          display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
          fontSize: 12, color: C.text, userSelect: "none", paddingBottom: 6,
        }}>
          <input
            type="checkbox"
            checked={includeResolues}
            onChange={(e) => setIncludeResolues(e.target.checked)}
            style={{ accentColor: C.accent, cursor: "pointer", width: 14, height: 14 }}
          />
          Inclure les alertes résolues
        </label>

        {/* Reset */}
        {(filSeverite || filType || filConducteur) && (
          <button
            onClick={() => { setFilSeverite(""); setFilType(""); setFilConducteur(""); }}
            style={{
              padding: "6px 12px", borderRadius: 8, border: `1px solid ${C.red}40`,
              background: C.redBg, color: C.red, fontSize: 11, fontWeight: 600, cursor: "pointer",
            }}
          >
            ✕ Réinitialiser
          </button>
        )}
      </div>

      {loadingListe && (
        <div style={{ textAlign: "center", padding: "12px 0", color: C.slate, fontSize: 13, marginBottom: 8 }}>
          Actualisation…
        </div>
      )}

      {/* ═══ LISTE ALERTES ACTIVES ═══ */}
      <SectionTitle color={C.red}>Alertes en cours</SectionTitle>
      {(liste?.alertes || []).length > 0 ? (
        <DrillDown
          title={`Alertes ${includeResolues ? "(actives + résolues)" : "actives"} (${liste.alertes.length})`}
          color={C.red}
          columns={colonnesAlertes}
          rows={liste.alertes}
        />
      ) : (
        <StatusMessage
          type="info"
          message={includeResolues ? "Aucune alerte ne correspond aux filtres." : "Aucune alerte active."}
          detail={includeResolues ? null : "Tous les seuils configurés sont respectés."}
        />
      )}

      {/* ═══ AGRÉGATS — par type + par conducteur ═══ */}
      <SectionTitle color={C.accent}>Répartition</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16, marginBottom: 16 }}>
        <MiniTable
          title="Par type d'alerte"
          color={C.accent}
          headers={[
            { key: "type_alerte", label: "Type", bold: true,
              render: (r) => typeLabel(r.type_alerte), total: () => "Total" },
            { key: "nb", label: "Total", align: "right", mono: true, bold: true,
              total: (rows) => rows.reduce((a, r) => a + (r.nb || 0), 0) },
            { key: "nb_critiques", label: "Critiques", align: "right", mono: true,
              color: (r) => (r.nb_critiques || 0) > 0 ? C.red : C.slateLight,
              total: (rows) => rows.reduce((a, r) => a + (r.nb_critiques || 0), 0) },
            { key: "nb_warning", label: "Warnings", align: "right", mono: true,
              color: (r) => (r.nb_warning || 0) > 0 ? C.accent : C.slateLight,
              total: (rows) => rows.reduce((a, r) => a + (r.nb_warning || 0), 0) },
          ]}
          rows={liste?.par_type || []}
        />

        <MiniTable
          title="Par conducteur"
          color={C.blue}
          headers={[
            { key: "conducteur", label: "Conducteur", bold: true, maxW: 200, total: () => "Total" },
            { key: "nb_chantiers", label: "Chantiers", align: "right", mono: true,
              total: (rows) => rows.reduce((a, r) => a + (r.nb_chantiers || 0), 0) },
            { key: "nb_alertes", label: "Alertes", align: "right", mono: true, bold: true,
              total: (rows) => rows.reduce((a, r) => a + (r.nb_alertes || 0), 0) },
            { key: "nb_critiques", label: "Critiques", align: "right", mono: true,
              color: (r) => (r.nb_critiques || 0) > 0 ? C.red : C.slateLight,
              total: (rows) => rows.reduce((a, r) => a + (r.nb_critiques || 0), 0) },
          ]}
          rows={liste?.par_conducteur || []}
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════
           DÉTECTION AUTOMATIQUE
         ═══════════════════════════════════════════════════════════════════════════ */}
      <SectionTitle color="#8B5CF6">Détection automatique</SectionTitle>

      {/* Bandeau seuils */}
      {detection?.seuils && (
        <div style={{
          padding: "10px 16px", marginBottom: 12, borderRadius: 10,
          background: C.bg, border: `1px solid ${C.border}`,
          fontSize: 12, color: C.slate, display: "flex", gap: 24, flexWrap: "wrap",
        }}>
          <span><strong style={{ color: C.text }}>Seuils configurés :</strong></span>
          {detection.seuils.marge != null && (
            <span>Marge nette min. : <strong style={{ color: C.text, fontFamily: mono }}>{detection.seuils.marge}%</strong></span>
          )}
          {detection.seuils.mo != null && (
            <span>Dépassement MO max. : <strong style={{ color: C.text, fontFamily: mono }}>{detection.seuils.mo}%</strong></span>
          )}
          {detection.seuils.sous_facturation != null && (
            <span>Écart sous-facturation : <strong style={{ color: C.text, fontFamily: mono }}>{detection.seuils.sous_facturation} pts</strong></span>
          )}
        </div>
      )}

      {/* MO dépassée */}
      <SubSectionTitle color={C.accent} badge={`${(detection?.mo_depassee || []).length} détectée(s)`}>
        MO dépassée
      </SubSectionTitle>
      {(detection?.mo_depassee || []).length > 0 ? (
        <MiniTable
          title={`Chantiers avec dépassement MO > ${detection.seuils?.mo ?? "?"}%`}
          color={C.accent}
          headers={[
            { key: "code_erp", label: "Code", bold: true, mono: true, total: () => "" },
            { key: "chantier", label: "Chantier", maxW: 280, total: () => "Total" },
            { key: "budget_mo", label: "Budget MO", align: "right", mono: true,
              render: (r) => r.budget_mo != null ? fmt(r.budget_mo) : "—",
              total: () => "" },
            { key: "avancement_pct", label: "Avanc.", align: "right", mono: true,
              render: (r) => r.avancement_pct != null ? `${Number(r.avancement_pct).toFixed(1)}%` : "—",
              total: () => "" },
            { key: "budget_avancement_mo", label: "Budget à avanc.", align: "right", mono: true,
              render: (r) => r.budget_avancement_mo != null ? fmt(r.budget_avancement_mo) : "—",
              total: () => "" },
            { key: "reel_mo", label: "Réel MO", align: "right", mono: true, bold: true,
              render: (r) => r.reel_mo != null ? fmt(r.reel_mo) : "—",
              total: () => "" },
            { key: "depassement_pct", label: "Dépassement", align: "right", mono: true, bold: true,
              render: (r) => r.depassement_pct != null
                ? <span style={{ color: C.red }}>+{Number(r.depassement_pct).toFixed(1)}%</span>
                : "—",
              total: () => "" },
          ]}
          rows={detection.mo_depassee}
        />
      ) : (
        <StatusMessage type="info" message="Aucun dépassement MO détecté." />
      )}

      {/* Marge faible */}
      <SubSectionTitle color={C.red} badge={`${(detection?.marge_faible || []).length} détectée(s)`}>
        Marge faible
      </SubSectionTitle>
      {(detection?.marge_faible || []).length > 0 ? (
        <MiniTable
          title={`Chantiers avec taux marge < ${detection.seuils?.marge ?? "?"}%`}
          color={C.red}
          headers={[
            { key: "code_erp", label: "Code", bold: true, mono: true, total: () => "" },
            { key: "chantier", label: "Chantier", maxW: 320, total: () => "Total" },
            { key: "marche_ht", label: "Marché HT", align: "right", mono: true,
              render: (r) => r.marche_ht != null ? fmt(r.marche_ht) : "—",
              total: (rows) => fmt(rows.reduce((a, r) => a + (r.marche_ht || 0), 0)) },
            { key: "debourse_ht", label: "Déboursé HT", align: "right", mono: true,
              render: (r) => r.debourse_ht != null ? fmt(r.debourse_ht) : "—",
              total: (rows) => fmt(rows.reduce((a, r) => a + (r.debourse_ht || 0), 0)) },
            { key: "marge_brute", label: "Marge brute", align: "right", mono: true, bold: true,
              render: (r) => r.marge_brute != null
                ? <span style={{ color: C.red }}>{fmt(r.marge_brute)}</span>
                : "—",
              total: (rows) => fmt(rows.reduce((a, r) => a + (r.marge_brute || 0), 0)) },
            { key: "taux_marge", label: "Taux marge", align: "right", mono: true, bold: true,
              render: (r) => {
                if (r.taux_marge == null) return "—";
                const pct = Number(r.taux_marge) > 1 ? Number(r.taux_marge) : Number(r.taux_marge) * 100;
                return <span style={{ color: C.red }}>{pct.toFixed(1)}%</span>;
              },
              total: () => "" },
          ]}
          rows={detection.marge_faible}
        />
      ) : (
        <StatusMessage type="info" message="Aucun chantier en marge faible." />
      )}

      {/* Intérim excessif */}
      <SubSectionTitle color="#F97316" badge={`${(detection?.interim_excessif || []).length} détectée(s)`}>
        Intérim excessif
      </SubSectionTitle>
      {(detection?.interim_excessif || []).length > 0 ? (
        <MiniTable
          title="Chantiers avec part intérim élevée"
          color="#F97316"
          headers={[
            { key: "code_erp", label: "Code", bold: true, mono: true, total: () => "" },
            { key: "chantier", label: "Chantier", maxW: 360, total: () => "Total" },
            { key: "heures_interim", label: "H. intérim", align: "right", mono: true, bold: true,
              render: (r) => r.heures_interim != null ? Number(r.heures_interim).toLocaleString("fr-FR") : "—",
              total: (rows) => Number(rows.reduce((a, r) => a + (r.heures_interim || 0), 0)).toLocaleString("fr-FR") },
            { key: "heures_totales", label: "H. totales", align: "right", mono: true,
              render: (r) => r.heures_totales != null ? Number(r.heures_totales).toLocaleString("fr-FR") : "—",
              total: (rows) => Number(rows.reduce((a, r) => a + (r.heures_totales || 0), 0)).toLocaleString("fr-FR") },
            { key: "ratio_interim", label: "Ratio", align: "right", mono: true, bold: true,
              render: (r) => {
                if (r.ratio_interim == null) return "—";
                const pct = Number(r.ratio_interim) > 1 ? Number(r.ratio_interim) : Number(r.ratio_interim) * 100;
                return <span style={{ color: "#F97316" }}>{pct.toFixed(1)}%</span>;
              },
              total: () => "" },
          ]}
          rows={detection.interim_excessif}
        />
      ) : (
        <StatusMessage type="info" message="Aucun excès d'intérim détecté." />
      )}

      {/* Sans facturation > 90j */}
      <SubSectionTitle color={C.blue} badge={`${(detection?.sans_facturation || []).length} détectée(s)`}>
        Sans facturation depuis &gt; 90 jours
      </SubSectionTitle>
      {(detection?.sans_facturation || []).length > 0 ? (
        <MiniTable
          title="Chantiers actifs sans facturation récente"
          color={C.blue}
          headers={[
            { key: "code_erp", label: "Code", bold: true, mono: true, total: () => "" },
            { key: "chantier", label: "Chantier", maxW: 360, total: () => "Total" },
            { key: "derniere_facture", label: "Dernière facture", mono: true,
              render: (r) => r.derniere_facture ? new Date(r.derniere_facture).toLocaleDateString("fr-FR") : "—",
              total: () => "" },
            { key: "jours_depuis", label: "Jours depuis", align: "right", mono: true, bold: true,
              render: (r) => r.jours_depuis != null
                ? <span style={{ color: C.blue }}>{r.jours_depuis} j</span>
                : "—",
              total: () => "" },
          ]}
          rows={detection.sans_facturation}
        />
      ) : (
        <StatusMessage type="info" message="Aucun chantier sans facturation prolongée." />
      )}

      {/* Écart chiffrage / marché */}
      <SubSectionTitle color="#8B5CF6" badge={`${(detection?.ecart_chiffrage_marche || []).length} détectée(s)`}>
        Écart chiffrage / marché
      </SubSectionTitle>
      {(detection?.ecart_chiffrage_marche || []).length > 0 ? (
        <MiniTable
          title="Dégradation marge entre chiffrage et marché signé"
          color="#8B5CF6"
          headers={[
            { key: "chiffrage_code", label: "Code chiffrage", bold: true, mono: true, total: () => "" },
            { key: "libelle", label: "Libellé", maxW: 360, total: () => "Total" },
            { key: "marge_chiffrage", label: "Marge chiffrage", align: "right", mono: true,
              render: (r) => {
                if (r.marge_chiffrage == null) return "—";
                const pct = Number(r.marge_chiffrage) > 1 ? Number(r.marge_chiffrage) : Number(r.marge_chiffrage) * 100;
                return `${pct.toFixed(1)}%`;
              },
              total: () => "" },
            { key: "marge_marche", label: "Marge marché", align: "right", mono: true,
              render: (r) => {
                if (r.marge_marche == null) return "—";
                const pct = Number(r.marge_marche) > 1 ? Number(r.marge_marche) : Number(r.marge_marche) * 100;
                return `${pct.toFixed(1)}%`;
              },
              total: () => "" },
            { key: "ecart_marge", label: "Écart marge", align: "right", mono: true, bold: true,
              render: (r) => {
                if (r.ecart_marge == null) return "—";
                const v = Number(r.ecart_marge);
                const isPct = Math.abs(v) <= 1;
                const display = isPct ? (v * 100).toFixed(1) : v.toFixed(1);
                return <span style={{ color: C.red }}>{display} pts</span>;
              },
              total: () => "" },
          ]}
          rows={detection.ecart_chiffrage_marche}
        />
      ) : (
        <StatusMessage type="info" message="Aucun écart chiffrage/marché détecté." />
      )}

      {/* ═══ HISTORIQUE ALERTES RÉSOLUES ═══ */}
      <SectionTitle color={C.green}>Historique des alertes résolues</SectionTitle>
      {(liste?.historique || []).length > 0 ? (
        <MiniTable
          title={`20 dernières alertes résolues (${liste.historique.length})`}
          color={C.green}
          headers={[
            { key: "severite", label: "Sévérité", render: (r) => <SeveriteBadge severite={r.severite} />, total: () => "" },
            { key: "chantier_code", label: "Code", bold: true, mono: true, total: () => "" },
            { key: "chantier", label: "Chantier", maxW: 220, total: () => "Total" },
            { key: "type_alerte", label: "Type", render: (r) => typeLabel(r.type_alerte), total: () => "" },
            { key: "conducteur", label: "Conducteur", maxW: 150, total: () => "" },
            { key: "date_declenchement", label: "Déclenchée", mono: true,
              render: (r) => r.date_declenchement ? new Date(r.date_declenchement).toLocaleDateString("fr-FR") : "—",
              total: () => "" },
            { key: "date_resolution", label: "Résolue", mono: true,
              render: (r) => r.date_resolution ? new Date(r.date_resolution).toLocaleDateString("fr-FR") : "—",
              total: () => "" },
            { key: "duree_resolution_jours", label: "Durée", align: "right", mono: true, bold: true,
              render: (r) => {
                let j = r.duree_resolution_jours;
                if (j == null && r.date_resolution && r.date_declenchement) {
                  j = Math.round((new Date(r.date_resolution) - new Date(r.date_declenchement)) / 86400000);
                }
                return j != null ? `${j} j` : "—";
              },
              total: () => "" },
          ]}
          rows={(liste.historique || []).slice(0, 20)}
        />
      ) : (
        <StatusMessage type="info" message="Aucune alerte résolue dans l'historique." detail="Les alertes apparaîtront ici une fois clôturées." />
      )}
    </div>
  );
}
